/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import { ChangeEventHandler, useEffect, useRef, useState } from "react";
import { assert } from "@itwin/core-bentley";
import { IModelApp } from "@itwin/core-frontend";
import { FormatterSpec, FormatType, ParserSpec } from "@itwin/core-quantity";
import { SchemaContext } from "@itwin/ecschema-metadata";
import { KoqPropertyValueFormatter } from "@itwin/presentation-common";
import { PropertyValueConstraints } from "../../common/ContentBuilder.js";
import { applyNumericConstraints, getMinMaxFromPropertyConstraints, getPersistenceUnitRoundingError } from "./Utils.js";

/**
 * Value of kind of quantity property.
 * @internal
 */
export interface QuantityValue {
  /** Raw value in persistence unit. */
  rawValue?: number;
  /** Formatted value with unit label based on active unit system or user input. With precision set to 12. */
  highPrecisionFormattedValue: string;
  /** Formatted value with unit label based on active unit system or user input. Default precision. */
  defaultFormattedValue: string;
  roundingError?: number;
}

/**
 * Props for [[useQuantityValueInput]]
 * @internal
 */
export interface UseQuantityValueInputProps {
  initialRawValue?: number;
  schemaContext: SchemaContext;
  koqName: string;
  constraints?: PropertyValueConstraints;
}

/**
 * Custom hook that manages state for quantity values input.
 * @internal
 */
export function useQuantityValueInput({
  initialRawValue,
  schemaContext,
  koqName,
  constraints,
}: UseQuantityValueInputProps) {
  interface State {
    quantityValue: QuantityValue;
    placeholder: string;
  }
  const initialRawValueRef = useRef(initialRawValue);
  const [{ quantityValue, placeholder }, setState] = useState<State>(() => ({
    quantityValue: {
      rawValue: initialRawValueRef.current,
      highPrecisionFormattedValue: "",
      defaultFormattedValue: "",
      roundingError: undefined,
    },
    placeholder: "",
  }));

  const onFormatterLoad = useRef<
    (props: {
      newDefaultFormatter: FormatterSpec;
      newHighPrecisionFormatter: FormatterSpec;
      newParser: ParserSpec;
    }) => void
  >(({ newHighPrecisionFormatter, newParser, newDefaultFormatter }) => {
    setState((prev): State => {
      /* v8 ignore next 1 -- @preserve */
      const defaultValue =
        prev.quantityValue.rawValue !== undefined
          ? newDefaultFormatter.applyFormatting(prev.quantityValue.rawValue)
          : `-- ${newHighPrecisionFormatter.unitConversions[0].label}`;
      const newFormattedValue =
        prev.quantityValue.rawValue !== undefined
          ? newHighPrecisionFormatter.applyFormatting(prev.quantityValue.rawValue)
          : newHighPrecisionFormatter.unitConversions[0].label;
      const placeholderUnit = newHighPrecisionFormatter.unitConversions[0].label;
      const roundingError = getPersistenceUnitRoundingError(newFormattedValue, newParser);

      return {
        ...prev,
        quantityValue: {
          ...prev.quantityValue,
          highPrecisionFormattedValue: newFormattedValue,
          defaultFormattedValue: defaultValue,
          roundingError,
        },
        placeholder: placeholderUnit,
      };
    });
  });

  const useFormatterAndParserResult = useFormatterAndParser(koqName, schemaContext, onFormatterLoad.current);

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    assert(useFormatterAndParserResult !== undefined); // input should be disabled if parser is `undefined`
    const { parser, defaultFormatter } = useFormatterAndParserResult;
    const newValue = e.currentTarget.value;
    const parseResult = parser.parseToQuantityValue(newValue);
    const roundingError = getPersistenceUnitRoundingError(newValue, parser);
    const defaultFormattedValue = parseResult.ok ? defaultFormatter?.applyFormatting(parseResult.value) : undefined;
    const rawValue = parseResult.ok
      ? applyNumericConstraints({ ...getMinMaxFromPropertyConstraints(constraints), value: parseResult.value })
      : undefined;

    setState(
      (prev): State => ({
        ...prev,
        quantityValue: {
          highPrecisionFormattedValue: newValue,
          defaultFormattedValue: defaultFormattedValue ?? newValue,
          rawValue,
          roundingError: parseResult.ok ? roundingError : undefined,
        },
      }),
    );
  };

  return { quantityValue, inputProps: { onChange, placeholder, disabled: !useFormatterAndParserResult } };
}

function useFormatterAndParser(
  koqName: string,
  schemaContext: SchemaContext,
  onChange: (props: {
    newDefaultFormatter: FormatterSpec;
    newHighPrecisionFormatter: FormatterSpec;
    newParser: ParserSpec;
  }) => void,
): { highPrecisionFormatter: FormatterSpec; parser: ParserSpec; defaultFormatter: FormatterSpec } | undefined {
  interface State {
    defaultFormatter: FormatterSpec;
    highPrecisionFormatter: FormatterSpec;
    parserSpec: ParserSpec;
  }
  const [state, setState] = useState<State>();

  useEffect(() => {
    const findFormatterAndParser = async () => {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const koqFormatter = new KoqPropertyValueFormatter(schemaContext, undefined, IModelApp.formatsProvider);
      const highPrecisionFormatter = await koqFormatter.getFormatterSpec({
        koqName,
        unitSystem: IModelApp.quantityFormatter.activeUnitSystem,
      });
      // formatter for default value should not have precision override
      const defaultFormatter = await koqFormatter.getFormatterSpec({
        koqName,
        unitSystem: IModelApp.quantityFormatter.activeUnitSystem,
      });
      const parserSpec = await koqFormatter.getParserSpec({
        koqName,
        unitSystem: IModelApp.quantityFormatter.activeUnitSystem,
      });
      if (highPrecisionFormatter && parserSpec && defaultFormatter) {
        if (highPrecisionFormatter.format.type === FormatType.Decimal) {
          highPrecisionFormatter.format.precision = 12;
        }
        onChange({
          newHighPrecisionFormatter: highPrecisionFormatter,
          newParser: parserSpec,
          newDefaultFormatter: defaultFormatter,
        });
        setState({ highPrecisionFormatter, parserSpec, defaultFormatter });
        return;
      }

      setState(undefined);
    };
    void findFormatterAndParser();

    const listeners = [
      IModelApp.quantityFormatter.onActiveFormattingUnitSystemChanged.addListener(findFormatterAndParser),
    ];
    if (IModelApp.formatsProvider) {
      listeners.push(IModelApp.formatsProvider.onFormatsChanged.addListener(findFormatterAndParser));
    }

    return () => {
      listeners.forEach((listener) => listener());
    };
  }, [koqName, schemaContext, onChange]);

  return state
    ? {
        highPrecisionFormatter: state.highPrecisionFormatter,
        parser: state.parserSpec,
        defaultFormatter: state.defaultFormatter,
      }
    : undefined;
}
